// src/services/api.js
import axios from 'axios';
import { fetchEventSource } from '@microsoft/fetch-event-source';

const api = axios.create({
  baseURL: '/',               // same-origin: proxied to the dashboard backend
  withCredentials: true,      // send the HTTP-only refreshToken cookie
});

let isRefreshing = false;
let queue = [];

const processQueue = (error, token = null) => {
  queue.forEach(p => error ? p.reject(error) : p.resolve(token));
  queue = [];
};

api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  err => Promise.reject(err)
);

api.interceptors.response.use(
  res => res,
  err => {
    const original = err.config;
    if (err.response?.status === 401 && !original._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          queue.push({ resolve, reject });
        })
          .then(token => {
            original.headers.Authorization = `Bearer ${token}`;
            return api(original);
          })
          .catch(e => Promise.reject(e));
      }

      original._retry = true;
      isRefreshing = true;

      return new Promise((resolve, reject) => {
        axios.post(
          '/api/auth/refresh',
          null,
          { baseURL: api.defaults.baseURL, withCredentials: true }
        )
        .then(({ data }) => {
          const { accessToken } = data;
          localStorage.setItem('token', accessToken);
          api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
          processQueue(null, accessToken);
          original.headers.Authorization = `Bearer ${accessToken}`;
          resolve(api(original));
        })
        .catch(refreshErr => {
          processQueue(refreshErr, null);
          reject(refreshErr);
        })
        .finally(() => {
          isRefreshing = false;
        });
      });
    }
    return Promise.reject(err);
  }
);

/**
 * Manually refreshes the auth token (for non-Axios flows).
 */
export async function refreshAuthToken() {
  const { data } = await axios.post(
    '/api/auth/refresh',
    null,
    { baseURL: api.defaults.baseURL, withCredentials: true }
  );
  const { accessToken } = data;
  localStorage.setItem('token', accessToken);
  api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  return accessToken;
}

/**
 * Subscribe to an SSE stream with built‑in auth + refresh logic.
 * @param {string} path      The SSE endpoint path
 * @param {object} handlers  { onmessage, onerror }
 * @returns {AbortController}
 */
export function subscribeEvents(path, { onmessage, onerror }) {
  const controller = new AbortController();

  const start = async () => {
    if (controller.signal.aborted) return;
    const token = localStorage.getItem('token');
    try {
      await fetchEventSource(path, {
        signal: controller.signal,
        headers: { Authorization: `Bearer ${token}` },
        async onopen(response) {
          if (response.ok && response.headers.get('content-type') === 'text/event-stream') {
            return; // connected successfully
          }
          // Attach status so the outer catch can inspect it
          const err = new Error(`SSE open failed: ${response.status}`);
          err.status = response.status;
          throw err;
        },
        onmessage,
        onerror(err) {
          // Stop the library's built-in retry loop; the outer catch handles restart
          throw err;
        }
      });
    } catch (err) {
      if (controller.signal.aborted) return;
      if (err.status === 401) {
        try {
          await refreshAuthToken();
          return start(); // restart with the new token
        } catch {
          onerror?.(err);
        }
      } else {
        onerror?.(err);
      }
    }
  };

  start();
  return controller;
}

export default api;
